import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, IndianRupee } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/date';
import { toast } from 'sonner';

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [searchTerm, payments]);

  const fetchPayments = async () => {
    try {
      const response = await api.get('/payments');
      setPayments(response.data);
      setFilteredPayments(response.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = () => {
    if (!searchTerm) {
      setFilteredPayments(payments);
      return;
    }
    const filtered = payments.filter(
      (payment) =>
        payment.booking_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPayments(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return '#8F9E8B';
      case 'pending':
        return '#C67D63';
      case 'failed':
        return '#EF4444';
      default:
        return '#64748B';
    }
  };

  const totalPaid = payments
    .filter((p) => p.payment_status === 'paid')
    .reduce((sum, p) => sum + p.amount, 0);

  const totalPending = payments
    .filter((p) => p.payment_status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="flex" style={{ background: '#F9F8F6' }}>
      <AdminSidebar />
      <div className="admin-content flex-1" data-testid="admin-payments">
        <div className="mb-8">
          <h1
            className="text-4xl font-bold mb-2"
            style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
          >
            Payments
          </h1>
          <p style={{ color: '#64748B' }}>Track all payments</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
            data-testid="total-paid-card"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: '#8F9E8B20', color: '#8F9E8B' }}
              >
                <IndianRupee size={28} />
              </div>
              <div>
                <p className="text-sm mb-1" style={{ color: '#64748B' }}>
                  Total Paid
                </p>
                <p className="text-3xl font-bold mono" style={{ color: '#1A3C34' }}>
                  ₹{totalPaid.toFixed(2)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm"
            data-testid="total-pending-card"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: '#C67D6320', color: '#C67D63' }}
              >
                <IndianRupee size={28} />
              </div>
              <div>
                <p className="text-sm mb-1" style={{ color: '#64748B' }}>
                  Total Pending
                </p>
                <p className="text-3xl font-bold mono" style={{ color: '#1A3C34' }}>
                  ₹{totalPending.toFixed(2)}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2"
              style={{ color: '#64748B' }}
            />
            <input
              type="text"
              placeholder="Search by payment or booking ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
              style={{ background: 'white' }}
              data-testid="search-payments"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p style={{ color: '#64748B' }}>Loading payments...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ background: '#F9F8F6' }}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Payment ID
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Booking ID
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Method
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, index) => (
                    <motion.tr
                      key={payment.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-t border-gray-100"
                      data-testid={`payment-row-${index}`}
                    >
                      <td className="px-6 py-4">
                        <span className="mono text-sm font-medium" style={{ color: '#1A202C' }}>
                          {payment.id.slice(0, 8)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="mono text-sm" style={{ color: '#64748B' }}>
                          {payment.booking_id.slice(0, 8)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-bold mono" style={{ color: '#1A3C34' }}>
                          ₹{payment.amount}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm capitalize" style={{ color: '#64748B' }}>
                          {payment.payment_method}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: `${getStatusColor(payment.payment_status)}20`,
                            color: getStatusColor(payment.payment_status),
                          }}
                        >
                          {payment.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#64748B' }}>
                          {formatDateTime(payment.created_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#64748B' }}>
                          Verified via Razorpay
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredPayments.length === 0 && (
              <div className="text-center py-20">
                <p style={{ color: '#64748B' }}>No payments found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPayments;